import React, { useEffect, useMemo } from "react";
import { Col, Row } from "react-styled-flexboxgrid";
import { ApiWorkers } from "../../../../../../api/workers";
import Modal from "../../../../../../components/Modal";
import { useApi } from "../../../../../../hooks/useApi";
import { useManage } from "../../../../../../hooks/useManage";
import { OPERATIONS } from "../../../../../../utils/constans";
import { parseJobsApplications } from "../../../../../../utils/helpers";
import Button from "../../../../../../components/Button";
import ButtonGroup from "../../../../../../components/ButtonGroup";
import RowSubComponent from "../RowSubComponent";
import { makeStyles } from "../../../../../../utils/styles";
import Box from "../../../../../../components/Box";
import {
    generateTablePdf,
    parseDataToPDFBody,
} from "../../../../../../utils/pdf";
import { toast } from "react-toastify";
import Icon from "../../../../../../components/Icon";
import useTheme from "../../../../../../hooks/useTheme";
import FormJobSettlement from "../../../../../../forms/FormJobSettlement";
import { format, fromUnixTime } from "date-fns";
import User from "../../../../../../components/User";
import Rating from "../../../../../../components/Rating";
import RatingStars from "../../../../../../components/RatingStars";
import Table from "../../../../../../components/Table";
import Typography from "../../../../../../components/Typography";
import Loader from "../../../../../../components/Loader";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((css) => ({
    boxAsync: css`
        position: relative;
    `,
}));

const RowSubComponentCompletedAsync = ({
    row,
    rowProps,
    refetchJobsCompleted,
    onClickDuplicate,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const styles = useStyles();
    const [{ operation }, setState] = useManage();

    const [
        { data: candidates, loading: candidatesLoading },
        getActiveCandidatesByJob,
    ] = useApi(ApiWorkers.getActiveCandidatesByJob, { manual: true });
    const [
        { loading: settlementCandidatesLoading },
        settlementCandidates,
    ] = useApi(ApiWorkers.settlementCandidates, { manual: true });

    // Memoize data
    const job = useMemo(
        () => ({
            ...row.original,
            applications: parseJobsApplications(
                row.original?.applications,
                candidates
            ),
        }),
        [row, candidates]
    );

    const jobTimeStart = useMemo(
        () => job?.time_start && fromUnixTime(job?.time_start),
        [job]
    );

    useEffect(() => {
        getActiveCandidatesByJob(job?.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClickSettlement = () => setState(OPERATIONS.SETTLEMENT);

    const handleClickCancelModal = () => setState(OPERATIONS.READ);

    const handleClickEdit = () => setState(OPERATIONS.SETTLEMENT);

    const handleClickGenerateReport = () => {
        const parsedData = parseDataToPDFBody(
            job?.applications,
            (application) => [
                application?.username,
                application?.user_candidate_rankings,
                application?.is_penalty ? t("tak") : t("nie"),
                application?.is_penalty
                    ? format(
                          fromUnixTime(application?.user_time_start),
                          "HH:mm"
                      )
                    : "",
                application?.is_penalty
                    ? format(fromUnixTime(application?.user_time_end), "HH:mm")
                    : "",
                application?.student,
                application?.candidate_ranking,
            ]
        );

        generateTablePdf({
            title: job?.place_id?.name,
            subtitle:
                t("Lista obecności: {{date}}", {
                    date: format(jobTimeStart, "dd.MM.yyyy"),
                }) + `, ${job?.time_range}`,
            rows: [
                {
                    title: t("Nazwa zlecenia"),
                    text: job?.title,
                },
                {
                    title: t("Stanowisko"),
                    text: job?.profession,
                },
                {
                    title: t("Manager"),
                    text: job?.supplier,
                },
                {
                    title: t("Liczba miejsc"),
                    text: job?.vacancies,
                },
                {
                    title: t("Liczba zgłoszeń"),
                    text: job?.vacancies_diff,
                },
            ],
            tableTitle: t("Oceny pracowników"),
            table: {
                head: [
                    [
                        t("Pracownik"),
                        t("Ranking pracownika"),
                        t("Obecność"),
                        t("Rozpoczęcie pracy"),
                        t("Zakończenie pracy"),
                        t("Aktywny student"),
                        t("Ocena pracownika"),
                    ],
                ],
                body: parsedData,
            },
            filename:
                t("Raport ocen {{date}}", {
                    values: { date: format(jobTimeStart, "dd.MM.yyyy") },
                }) + `, ${job?.time_range}`,
        });
    };

    const handleClickDuplicate = (id) => () => onClickDuplicate(id);

    const handleSubmitSettlement = async (data) => {
        const res = await settlementCandidates(data);
        if (res?.data) {
            toast.success(t("Rozliczenie zlecenia zostało zapisane"));
            setState(OPERATIONS.READ, null);
            await refetchJobsCompleted();
        }
    };

    const columns = useMemo(
        () => [
            {
                Header: t("Pracownik"),
                accessor: "username",
                Cell: ({ row }) => {
                    return <User user={row.original?.user_candidate} />;
                },
            },
            {
                Header: t("Ranking pracownika"),
                accessor: "user_candidate_rankings",
                Cell: ({ cell: { value } }) => <Rating rate={value} />,
            },
            {
                Header: t("Obecność"),
                accessor: "is_penalty",
                Cell: ({ cell: { value } }) => {
                    return value ? (
                        <Icon name="checkbox" width="20" height="20" />
                    ) : (
                        ""
                    );
                },
            },
            {
                Header: t("Rozpoczęcie pracy"),
                accessor: "user_time_start",
                Cell: ({ row, cell: { value } }) => {
                    return row.original?.is_penalty
                        ? format(fromUnixTime(value), "HH:mm")
                        : "";
                },
            },
            {
                Header: t("Zakończenie pracy"),
                accessor: "user_time_end",
                Cell: ({ row, cell: { value } }) => {
                    return row.original?.is_penalty
                        ? format(fromUnixTime(value), "HH:mm")
                        : "";
                },
            },
            {
                Header: t('Aktywny student'),
                accessor: 'student', 
                width: 60
            },
            {
                Header: t("Ocena pracownika"),
                accessor: "candidate_ranking",
                Cell: ({ cell: { value } }) => {
                    return <RatingStars small rate={value} />;
                },
            },
        ],
        [t]
    );

    return (
        <Box customStyle={styles.boxAsync}>
            <RowSubComponent
                row={row}
                rowProps={rowProps}
                footerContent={
                    <Row end="xs">
                        <Col>
                            <ButtonGroup gutters={10}>
                                <Button
                                    variant="text"
                                    startIcon={
                                        <Icon
                                            name="order"
                                            width={14}
                                            height={14}
                                        />
                                    }
                                    onClick={handleClickDuplicate(
                                        row?.original?.id
                                    )}
                                >
                                    {t("DODAJ PODOBNE")}
                                </Button>
                                {job?.applications.length > 0 &&
                                    row.original?.is_settled && (
                                        <Button
                                            variant="contained"
                                            startIcon={
                                                <Icon
                                                    name="report"
                                                    width={16}
                                                    height={14}
                                                    fill={theme.colors.white}
                                                />
                                            }
                                            onClick={handleClickGenerateReport}
                                        >
                                            {t("Generuj raport ze zlecenia")}
                                        </Button>
                                    )}

                                {job?.applications.length > 0 &&
                                    row.original?.is_settled && (
                                        <Button
                                            variant="contained"
                                            startIcon={
                                                <Icon
                                                    name="report"
                                                    width={16}
                                                    height={14}
                                                    fill={theme.colors.white}
                                                />
                                            }
                                            onClick={handleClickEdit}
                                        >
                                            {t("Edytuj zlecenie")}
                                        </Button>
                                    )}
                                {job?.applications.length > 0 &&
                                    !row.original?.is_settled && (
                                        <Button
                                            variant="contained"
                                            startIcon={
                                                <Icon
                                                    name="star"
                                                    width={10}
                                                    height={10}
                                                    fill={theme.colors.white}
                                                />
                                            }
                                            onClick={handleClickSettlement}
                                        >
                                            {t(
                                                "ROZLICZ CZAS PRACY I OCEŃ PRACOWNIKÓW"
                                            )}
                                        </Button>
                                    )}
                            </ButtonGroup>
                        </Col>
                    </Row>
                }
            >
                {row.original?.is_settled && (
                    <>
                        <Typography>{t("Oceny pracowników")}</Typography>
                        <Table
                            columns={columns}
                            data={job?.applications}
                            hideFooter
                        />
                    </>
                )}
            </RowSubComponent>
            <Modal open={operation === OPERATIONS.SETTLEMENT}>
                <FormJobSettlement
                    loading={settlementCandidatesLoading}
                    job={job}
                    onSubmit={handleSubmitSettlement}
                    onClickCancel={handleClickCancelModal}
                />
            </Modal>
            <Loader show={candidatesLoading} />
        </Box>
    );
};

export default RowSubComponentCompletedAsync;
